// Diagnósticos · Embudo por programa. Totales y tasas los calcula el backend en
// código (/programs/{id}/funnel); aquí solo se dibujan.
import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import * as api from '../api';
import type { DiagProgram, Funnel } from '../types';
import { Btn, Empty, ErrorBox, Panel, PROPOSITO_LABEL, Spinner, inputCls } from './ui';

const STEP_LABEL: Record<string, string> = {
  registrados: 'Registrados',
  invitados: 'Invitados',
  iniciaron: 'Iniciaron',
  completaron: 'Completaron',
  invitados_agregados: 'Invitados del equipo',
  resultados_liberados: 'Resultados enviados',
  compraron: 'Compraron',
};

export function FunnelView() {
  const [programs, setPrograms] = useState<DiagProgram[]>([]);
  const [programId, setProgramId] = useState('');
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.listPrograms(true)
      .then((r) => { setPrograms(r.programs); setProgramId((id) => id || r.programs[0]?.id || ''); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  const load = async (id: string) => {
    if (!id) return;
    setError('');
    setFunnel(null);
    try {
      setFunnel(await api.getFunnel(id));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => { void load(programId); }, [programId]);

  if (loading) return <Spinner />;
  const max = Math.max(1, ...(funnel?.steps.map((s) => s.count ?? 0) ?? [1]));
  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Embudo</h2>
          <p className="text-sm text-muted-foreground">De registro a compra, por programa.</p>
        </div>
        <div className="flex gap-2">
          <select className={`${inputCls} w-auto`} value={programId} onChange={(e) => setProgramId(e.target.value)} aria-label="Programa">
            {programs.map((p) => <option key={p.id} value={p.id}>{p.name} · {PROPOSITO_LABEL[p.proposito]}</option>)}
          </select>
          <Btn onClick={() => load(programId)}><RefreshCw className="h-3.5 w-3.5" /></Btn>
        </div>
      </div>
      <ErrorBox error={error} />
      {!programs.length ? <Empty>Crea un programa para ver su embudo.</Empty> : !funnel ? (!error && <Spinner />) : (
        <>
          <Panel>
            <div className="space-y-2">
              {funnel.steps.map((s) => (
                <div key={s.key} className="grid grid-cols-[150px_1fr_110px] items-center gap-3 text-sm">
                  <span className="text-foreground">{STEP_LABEL[s.key] ?? s.key}</span>
                  <div className="h-6 overflow-hidden rounded-md bg-muted">
                    {s.count !== null && <div className={`h-full rounded-md ${s.key === 'invitados_agregados' ? 'bg-primary/40' : 'bg-primary'}`} style={{ width: `${Math.max(2, (100 * (s.count ?? 0)) / max)}%` }} />}
                  </div>
                  <span className="text-right tabular-nums text-foreground">
                    {s.count === null ? '—' : s.count}
                    {s.pct_of_prev !== null && <span className="ml-1 text-xs text-muted-foreground">({s.pct_of_prev}%)</span>}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">Entre paréntesis: % respecto al paso anterior. «Invitados del equipo» es un conteo aparte (personas que agregaron los dueños).</p>
          </Panel>
          <div className="grid gap-3 sm:grid-cols-4">
            {[
              ['Proyectos', funnel.projects_total], ['Con consentimiento', funnel.con_consentimiento ?? '—'],
              ['Analizados', funnel.analizados], ['Invitados que terminaron', funnel.invitados_completaron],
            ].map(([k, v]) => (
              <div key={String(k)} className="rounded-xl border border-border bg-card p-3">
                <div className="text-xs text-muted-foreground">{k}</div>
                <div className="text-xl font-semibold tabular-nums text-foreground">{String(v)}</div>
              </div>
            ))}
          </div>
          {funnel.notas.length > 0 && <div className="space-y-1 text-xs text-muted-foreground">{funnel.notas.map((n) => <p key={n}>{n}</p>)}</div>}
        </>
      )}
    </div>
  );
}
